import { message, Modal } from 'antd';
import axios from 'axios';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';

// Tạo instance axios
const request = axios.create({
    baseURL: 'http://localhost:9900/api/v1',
    // timeout: 300000, // timeout 300s
});

// Biến để track trạng thái hiển thị modal
let isModalVisible = false;
let isNetworkErrorVisible = false;

// Error messages
const ERROR_MESSAGES = {
    NETWORK_ERROR: 'Không thể kết nối đến máy chủ. Vui lòng kiểm tra kết nối của bạn.',
    TIMEOUT: 'Yêu cầu đã hết thời gian chờ. Vui lòng thử lại.',
    SERVER_ERROR: 'Đã có lỗi xảy ra từ máy chủ.',
    UNAUTHORIZED: 'Bạn cần đăng nhập lại.',
    FORBIDDEN: 'Bạn không có quyền thực hiện thao tác này.',
};

export const useAxiosInterceptor = () => {
    const navigate = useNavigate();
    const interceptorRef = useRef(null);

    useEffect(() => {
        // Request interceptor
        request.interceptors.request.use(
            (config) => {
                // Có thể thêm xử lý chung cho request ở đây
                return config;
            },
            (error) => {
                return Promise.reject(error);
            },
        );

        // Response interceptor
        interceptorRef.current = request.interceptors.response.use(
            (response) => response,
            (error) => {
                // Xử lý lỗi timeout
                if (error.code === 'ECONNABORTED') {
                    if (!isNetworkErrorVisible) {
                        isNetworkErrorVisible = true;
                        Modal.error({
                            title: 'Lỗi kết nối',
                            content: ERROR_MESSAGES.TIMEOUT,
                            onOk: () => {
                                isNetworkErrorVisible = false;
                            },
                            afterClose: () => {
                                isNetworkErrorVisible = false;
                            },
                        });
                    }
                    return Promise.reject({
                        status: 'TIMEOUT_ERROR',
                        message: ERROR_MESSAGES.TIMEOUT,
                    });
                }

                // Xử lý lỗi network
                if (!error.response && !isNetworkErrorVisible) {
                    isNetworkErrorVisible = true;
                    Modal.error({
                        title: 'Lỗi kết nối',
                        content: ERROR_MESSAGES.NETWORK_ERROR,
                        onOk: () => {
                            isNetworkErrorVisible = false;
                        },
                        afterClose: () => {
                            isNetworkErrorVisible = false;
                        },
                    });
                    return Promise.reject({
                        status: 'NETWORK_ERROR',
                        message: ERROR_MESSAGES.NETWORK_ERROR,
                    });
                }

                // Xử lý các lỗi có response
                if (error.response) {
                    const { status, data } = error.response;

                    switch (status) {
                        case 401:
                            if (!isModalVisible) {
                                isModalVisible = true;
                                // Bỏ qua một số error code đặc biệt
                                if (data?.errorCode === 'NMP-00000008') {
                                    return Promise.reject(error);
                                }
                                Modal.error({
                                    title: 'Lỗi xác thực',
                                    content: ERROR_MESSAGES.UNAUTHORIZED,
                                    okText: 'Đăng nhập lại',
                                    onOk: () => {
                                        isModalVisible = false;
                                        navigate('/login');
                                    },
                                    afterClose: () => {
                                        isModalVisible = false;
                                    },
                                });
                            }
                            break;

                        case 403:
                            if (!isModalVisible) {
                                isModalVisible = true;
                                if (data?.errorCode === 'NMP-00000001') {
                                    return Promise.reject(error);
                                }
                                if (data?.errorCode === 'NMP-00000004') {
                                    Modal.error({
                                        title: 'Không có quyền',
                                        content: ERROR_MESSAGES.FORBIDDEN,
                                        onOk: () => {
                                            isModalVisible = false;
                                        },
                                        afterClose: () => {
                                            isModalVisible = false;
                                        },
                                    });
                                }
                            }
                            break;

                        case 404:
                            if (data?.errorCode === 'NMP-00000010') {
                                navigate('/404');
                            }
                            break;

                        default:
                            // Xử lý các lỗi khác (500, etc)
                            if (status >= 500) {
                                message.error(ERROR_MESSAGES.SERVER_ERROR);
                            } else if (![400, 401, 403, 404].includes(status)) {
                                message.error(error.response?.statusText || ERROR_MESSAGES.SERVER_ERROR);
                            }
                    }
                }
                return Promise.reject(error);
            },
        );

        return () => {
            if (interceptorRef.current !== null) {
                request.interceptors.response.eject(interceptorRef.current);
            }
        };
    }, [navigate]);
};

export default request;
